import React, { useMemo } from 'react';
import {
  Page,
  Document,
  StyleSheet,
  View,
  Font,
  Text,
  Image,
  Svg,
  Path,
} from '@react-pdf/renderer';
import logo from 'assets/logos/r.World-company-logo.png';
import { Order } from 'types/order';
import { convertUTCToLocal } from 'utils/dateUtils';
import {
  calculateItemsPerPackage,
  calculateTotalItems,
} from 'utils/itemsHelper';
import { useSKU } from 'hooks';
import { SKU } from 'types/sku';
import StarBadge from './SVG/StarBadge';
import Case from './SVG/Case';
import Truck from './SVG/Truck';
import moment from 'moment';

Font.register({
  family: 'Trebuchet',
  fonts: [
    {
      src: require('assets/impact-report/trebucbd.ttf'),
      fontWeight: 'bold',
    },
    {
      src: require('assets/impact-report/trebuc.ttf'),
      fontWeight: 'normal',
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    paddingTop: 30,
    paddingBottom: 30,
  },
  tableContainer: {
    padding: 24,
    paddingBottom: 20,
  },
  table: {
    width: 'auto',
    marginVertical: 10,
    borderWidth: 1,
    borderColor: '#4CAF50',
  },
  tableRow: {
    flexDirection: 'row',
  },
  header: {
    backgroundColor: '#f6fffa',
    fontWeight: 'bold',
  },
  footer: {
    backgroundColor: '#d2f8d2',
    fontWeight: 'bold',
  },
  cell: {
    borderRightWidth: 1,
    borderRightColor: '#4CAF50',
    padding: 6,
    flexGrow: 1,
    textAlign: 'center',
  },
  lastCell: {
    borderRightWidth: 0,
  },
  boldText: {
    fontWeight: 'bold',
  },
  rightAlign: {
    textAlign: 'right',
  },
});

type PdfHarvestSummaryReportProps = {
  data: Order;
  skuData: SKU[];
};

type OrderDetailsPageProps = {
  data: Order;
  skuData: SKU[];
};

export const OrderDetailsPage = ({ data, skuData }: OrderDetailsPageProps) => {
  const details = data?.details || [];
  const deliveryDate = convertUTCToLocal(
    data?.deliveryAt,
    true,
    true,
  ) as string;
  const totalCases = details?.reduce((acc, cur) => acc + cur.casesCount, 0);

  const allSkuDetails = data?.skuDetails || [];
  const skuNameMapping: { [key: string]: string } = {};
  allSkuDetails.map((s) => (skuNameMapping[s._id] = s.name));

  const { totalBoxQuantity, totalItemsQuantity } = useMemo(() => {
    return details.reduce(
      (totals, d) => {
        const sku = skuData?.find((s) => s?._id === d.sku);
        const itemsPerPackage = calculateItemsPerPackage(sku);
        const totalItemsForCurrentDetail = calculateTotalItems(
          d.casesCount,
          itemsPerPackage,
        );

        return {
          totalBoxQuantity:
            totals.totalBoxQuantity + (Number(d.casesCount) || 0),
          totalItemsQuantity:
            totals.totalItemsQuantity + totalItemsForCurrentDetail,
        };
      },
      { totalBoxQuantity: 0, totalItemsQuantity: 0 },
    );
  }, [data, details, skuData]);

  return (
    <Page
      size="A4"
      style={{
        backgroundColor: '#fff',
      }}>
      <View
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          height: '100%',
          justifyContent: 'space-between',
        }}>
        <View
          style={{
            padding: '28px',
            gap: '16px',
            border: '1px solid #FFF',
          }}>
          <View
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}>
            <View>
              <Text style={{ fontSize: '24px', fontWeight: 'bold' }}>
                Delivery Receipt
              </Text>
              <Text style={{ fontSize: '12px', marginTop: '2px' }}>
                Order no: {data.orderNumber}
              </Text>
              <Text style={{ fontSize: '12px', marginTop: '2px' }}>
                Delivery Date: {deliveryDate}
              </Text>
            </View>
            <Image src={logo} style={{ height: '59px' }} />
          </View>
          <View
            style={{
              height: '1px',
              width: '100%',
              backgroundColor: '#000',
            }}
          />
          <View
            style={{
              justifyContent: 'space-between',
              borderRadius: '5px',
              borderWidth: '0.6px',
              borderColor: '#EBEFF6',
              padding: '15px',
              flexDirection: 'row',
            }}>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '6px',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <StarBadge />
              <View
                style={{
                  gap: '4px',
                }}>
                <Text style={{ fontSize: '10px', color: '#868DA6' }}>Site</Text>
                <Text style={{ fontSize: '10px' }}>{data?.site?.name}</Text>
              </View>
            </View>

            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '6px',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <Truck />
              <View style={{ gap: '4px' }}>
                <Text style={{ fontSize: '10px', color: '#868DA6' }}>
                  Delivery Date:
                </Text>
                <Text style={{ fontSize: '10px' }}>{deliveryDate}</Text>
              </View>
            </View>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                gap: '6px',
                justifyContent: 'center',
                alignItems: 'center',
              }}>
              <Case />
              <View style={{ gap: '4px' }}>
                <Text style={{ fontSize: '10px', color: '#868DA6' }}>
                  Total Cases:
                </Text>
                <Text style={{ fontSize: '10px' }}>{totalCases}</Text>
              </View>
            </View>
          </View>
          <View
            style={{
              border: '1px solid #56A666',
              borderRadius: '5px',
            }}>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                paddingHorizontal: '8px',
              }}>
              <View style={{ width: '40%', padding: '8px' }}>
                <Text style={{ fontSize: '10px', fontWeight: 'bold' }}>
                  Package SKU
                </Text>
              </View>
              <View
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'row',
                }}>
                <View style={{ flex: 1, padding: '8px' }}>
                  <Text style={{ fontSize: '10px', fontWeight: 'bold' }}>
                    Case Quantity
                  </Text>
                </View>
                <View style={{ flex: 1, padding: '8px' }}>
                  <Text style={{ fontSize: '10px', fontWeight: 'bold' }}>
                    Items per Package
                  </Text>
                </View>
                <View style={{ flex: 1, padding: '8px' }}>
                  <Text style={{ fontSize: '10px', fontWeight: 'bold' }}>
                    Total Items
                  </Text>
                </View>
              </View>
            </View>

            {details?.map((detail) => {
              const sku = skuData?.find((s) => s?._id === detail?.sku);
              const itemsPerPackage = sku ? calculateItemsPerPackage(sku) : 0;
              const totalItems =
                detail?.casesCount && itemsPerPackage
                  ? calculateTotalItems(detail.casesCount, itemsPerPackage)
                  : 0;
              return (
                <View style={{ borderTop: '1px solid #56A666' }}>
                  <View
                    style={{
                      display: 'flex',
                      paddingHorizontal: '8px',
                      flexDirection: 'row',
                    }}>
                    <View style={{ width: '40%', padding: '8px' }}>
                      <Text style={{ fontSize: '8px', fontWeight: 'bold' }}>
                        {skuNameMapping[detail.sku]}
                      </Text>
                    </View>
                    <View
                      style={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'row',
                      }}>
                      <View style={{ flex: 1, padding: '8px' }}>
                        <Text style={{ fontSize: '8px', fontWeight: 'bold' }}>
                          {detail.casesCount}
                        </Text>
                      </View>
                      <View style={{ flex: 1, padding: '8px' }}>
                        <Text style={{ fontSize: '8px', fontWeight: 'bold' }}>
                          {itemsPerPackage}
                        </Text>
                      </View>
                      <View style={{ flex: 1, padding: '8px' }}>
                        <Text style={{ fontSize: '8px', fontWeight: 'bold' }}>
                          {totalItems}
                        </Text>
                      </View>
                    </View>
                  </View>
                </View>
              );
            })}
            <View style={{ borderTop: '1px solid #56A666' }}>
              <View
                style={{
                  display: 'flex',
                  backgroundColor: '#D0F5D8',
                  paddingHorizontal: '8px',
                  borderBottomLeftRadius: '4px',
                  borderBottomRightRadius: '4px',
                  flexDirection: 'row',
                }}>
                <View style={{ width: '40%', padding: '8px' }}>
                  <Text style={{ fontSize: '10px', fontWeight: 'bold' }}>
                    Total
                  </Text>
                </View>
                <View
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'row',
                  }}>
                  <View style={{ flex: 1, padding: '8px' }}>
                    <Text
                      style={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                      }}>
                      {totalBoxQuantity}
                    </Text>
                  </View>
                  <View style={{ flex: 1, padding: '8px' }}>
                    <Text
                      style={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                      }}>
                      -
                    </Text>
                  </View>
                  <View style={{ flex: 1, padding: '8px' }}>
                    <Text
                      style={{
                        fontSize: '10px',
                        fontWeight: 'bold',
                      }}>
                      {totalItemsQuantity}
                    </Text>
                  </View>
                </View>
              </View>
            </View>
          </View>
          <View style={{ gap: '2px' }}>
            <Text
              style={{
                fontSize: '8px',
                color: '#868DA6',
                paddingHorizontal: '16px',
              }}>
              CUSTOMER ORDER NOTES
            </Text>
            <View
              style={{
                borderRadius: '5px',
                borderWidth: '0.6px',
                marginTop: '10px',
                borderColor: '#EBEFF6',
                backgroundColor: '#EBEFF6',
                gap: '4px',
                padding: '10px',
                minHeight: '73px',
              }}>
              <Text style={{ fontSize: '8px' }}>{data.notes}</Text>
            </View>
          </View>
        </View>
        <View
          style={{
            padding: '28px',
            gap: '16px',
          }}>
          <View
            style={{
              width: '100%',
              height: '1px',
              backgroundColor: '#000',
            }}
          />
          <View
            style={{
              gap: '20px',
            }}>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: '20px',
              }}>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '6px',
                  alignItems: 'flex-end',
                }}>
                <Text style={{ fontSize: '8px' }}>DELIVERED BY</Text>
                <View
                  style={{
                    width: '120px',
                    height: '1px',
                    backgroundColor: '#000',
                  }}
                />
              </View>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '6px',
                  alignItems: 'flex-end',
                }}>
                <Text style={{ fontSize: '8px' }}>RECIEVED BY</Text>
                <View
                  style={{
                    width: '120px',
                    height: '1px',
                    backgroundColor: '#000',
                  }}
                />
              </View>
            </View>
            <View
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-end',
                gap: '20px',
              }}>
              <View
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  gap: '6px',
                  alignItems: 'flex-end',
                }}>
                <Text style={{ fontSize: '8px' }}>CUSTOMER SIGN</Text>
                <View
                  style={{
                    width: '120px',
                    height: '1px',
                    backgroundColor: '#000',
                  }}
                />
              </View>
            </View>
            <View>
              <Text
                style={{
                  fontSize: '8px',
                  marginBottom: '10px',
                  color: '#868DA6',
                }}>
                COMMENTS
              </Text>
              <View
                style={{
                  borderRadius: '5px',
                  borderWidth: '0.6px',
                  height: '80px',
                  borderColor: '#B9B9B9',
                  padding: '10px',
                }}></View>
            </View>
          </View>
        </View>
      </View>
    </Page>
  );
};

const OrderDetailsPdfTemplate = ({
  data,
  skuData,
}: PdfHarvestSummaryReportProps) => {
  return (
    <Document>
      <OrderDetailsPage data={data} skuData={skuData} />
    </Document>
  );
};

export default OrderDetailsPdfTemplate;
